import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { FlexGap, PlaybackPauseIcon, PlaybackPlayIcon, tokens } from '@treams/treams-ui';

const PlayerContainer = styled(FlexGap)`
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.compact}px ${tokens.padding.control}px;
	background-color: ${tokens.brand.background};
	border: 1px solid ${tokens.brand.border};
	align-items: center;
`;

const PlayPauseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: ${tokens.radius.full}px;
	border: none;
	background-color: ${tokens.surface.white};
	color: ${tokens.brand.icon};
	cursor: pointer;
	flex-shrink: 0;
	transition: background-color 0.15s ease;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}

	&:hover {
		background-color: ${tokens.brand.hover};
	}
`;

const ProgressBarContainer = styled.div`
	flex: 1;
	height: 4px;
	border-radius: ${tokens.radius.xs}px;
	background-color: ${tokens.brand.hover};
	cursor: pointer;
	position: relative;
`;

const ProgressBarFill = styled.div<{ $progress: number }>`
	height: 100%;
	border-radius: ${tokens.radius.xs}px;
	background-color: ${tokens.brand.icon};
	width: ${({ $progress }) => $progress * 100}%;
	transition: width 0.1s linear;
`;

const TimeLabel = styled.span`
	font-size: ${tokens.typography.sizes.small}px;
	color: ${tokens.brand.text};
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
`;

function formatTime(seconds: number): string {
	if (!Number.isFinite(seconds)) {
		return '0:00';
	}
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const AudioPlayer = ({ src }: { src: string }) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [playing, setPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);

	useEffect(() => {
		const audio = new Audio();
		audio.preload = 'metadata';
		audio.src = src;
		audioRef.current = audio;

		// Files recorded via MediaRecorder (e.g. WebM) often omit the duration in
		// their metadata, so `audio.duration` reports `Infinity`. Seeking to a very
		// large time forces the browser to scan to the end and emit `durationchange`
		// with the real value, which we then reset back to the start.
		let seekingForDuration = false;

		const handleLoadedMetadata = () => {
			if (Number.isFinite(audio.duration)) {
				setDuration(audio.duration);
			} else {
				seekingForDuration = true;
				audio.currentTime = Number.MAX_SAFE_INTEGER;
			}
		};

		const handleDurationChange = () => {
			if (seekingForDuration && Number.isFinite(audio.duration)) {
				seekingForDuration = false;
				setDuration(audio.duration);
				audio.currentTime = 0;
			}
		};

		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('durationchange', handleDurationChange);
		audio.addEventListener('timeupdate', () => {
			if (seekingForDuration) return;
			setCurrentTime(audio.currentTime);
			if (audio.duration > 0 && Number.isFinite(audio.duration)) {
				setProgress(audio.currentTime / audio.duration);
			}
		});
		audio.addEventListener('ended', () => {
			setPlaying(false);
			setProgress(0);
			setCurrentTime(0);
		});

		return () => {
			audio.pause();
			audio.src = '';
		};
	}, [src]);

	const togglePlayPause = useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;

		if (playing) {
			audio.pause();
			setPlaying(false);
		} else {
			audio.play();
			setPlaying(true);
		}
	}, [playing]);

	const handleSeek = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const audio = audioRef.current;
			if (!audio || !duration) return;

			const rect = e.currentTarget.getBoundingClientRect();
			const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
			audio.currentTime = ratio * duration;
		},
		[duration],
	);

	return (
		<PlayerContainer gap={10}>
			<PlayPauseButton onClick={togglePlayPause} type="button">
				{playing ? <PlaybackPauseIcon /> : <PlaybackPlayIcon />}
			</PlayPauseButton>
			<TimeLabel>{formatTime(currentTime)}</TimeLabel>
			<ProgressBarContainer onClick={handleSeek}>
				<ProgressBarFill $progress={progress} />
			</ProgressBarContainer>
			<TimeLabel>{formatTime(duration)}</TimeLabel>
		</PlayerContainer>
	);
};
